// packages/web-ui/src/hooks/useMentionAutocomplete.ts
//
// Story W4-S6 (W4-S6 / 4-6) — `useMentionAutocomplete`. React hook that
// drives the SPA chat composer's `<MentionPopup>`:
//
//   1. Detects the active `@`-trigger in the textarea (caret-aware).
//   2. Debounces 150 ms (PRD FR-CHAT-3 line 215).
//   3. Resolves candidates from a configurable async source — production
//      wires `kbApi.listEntities` (server-side substring search, the
//      W4-S6 helper-lift route is owned by the server-side dev agent and
//      not yet plumbed into a typed client). Tests pass an inline source.
//   4. Ranks the response via the base `rankCandidates` helper from
//      `@tessa-lean/base`. W4 fix-pass HIGH-E (review HIGH-3 / FR-KB-4)
//      switched the import from the local SPA mirror at
//      `components/mention-helper/rank-mentions.ts` (kept as a deprecated
//      shim for one release window) so the SPA, the legacy Lit chat-input,
//      and the server's `/kb/mentions` route all share one ranking
//      implementation.
//
// The hook is *self-contained*: it does NOT touch the chat zustand slice
// (the composer story W2-S7 is owned by a different parallel agent).
// Consumers wire `inputValue` / `caret` to their textarea and feed the
// emitted state into `<MentionPopup>` props.

import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  KbEntity,
  KbEntityKind,
  MentionCandidate,
} from '@tessa-lean/base/api-schemas/index.js';
import {
  rankCandidates,
  DEFAULT_LIMIT as MENTION_DEFAULT_LIMIT,
} from '@tessa-lean/base/mention-autocomplete';

/** Debounce window per PRD FR-CHAT-3. */
export const MENTION_DEBOUNCE_MS = 150;

/** Returned to consumers; describes one resolved `@`-trigger in the input. */
export interface MentionTrigger {
  /** Char offset of the `@` in the source string. */
  atOffset: number;
  /** Text after the `@` up to the caret (the prefix used for ranking). */
  prefix: string;
  /** Caret offset (one past the last prefix char). */
  caretOffset: number;
}

export interface UseMentionAutocompleteResult {
  /** True when the popup should render. */
  open: boolean;
  /** Active prefix (post-`@` text up to caret). Empty string when closed. */
  prefix: string;
  /** Ranked candidates after debounced fetch. */
  candidates: ReadonlyArray<MentionCandidate>;
  /** Active row index inside the popup's filtered list. */
  activeIndex: number;
  /** Active kind filter. `undefined` means All. */
  activeFilter: KbEntityKind | undefined;
  /** Loading state — true while a fetch is in flight. */
  loading: boolean;
  /** Error message; `null` when no error. */
  error: string | null;

  /** The currently-resolved `@`-trigger (offsets into the source string). */
  trigger: MentionTrigger | null;

  /**
   * Replaces the `@<prefix>` substring with `replacement` and returns the
   * new source string. Composers use this from `onSelect` to insert a
   * chip placeholder.
   */
  applyReplacement: (source: string, replacement: string) => string;

  /** Setters consumed by `<MentionPopup>` props (controlled). */
  setActiveIndex: (i: number) => void;
  setActiveFilter: (kind: KbEntityKind | undefined) => void;
  /** Force-close without selection (Esc / click-outside). */
  dismiss: () => void;
}

/**
 * Async candidate resolver. Producers return a list of `KbEntity` for the
 * given prefix; the hook handles ranking + chip-text mapping. The signal
 * lets callers cancel the request when the prefix changes mid-flight.
 */
export type MentionCandidateFetcher = (
  prefix: string,
  signal: AbortSignal,
) => Promise<ReadonlyArray<KbEntity>>;

export interface UseMentionAutocompleteOptions {
  /** Source string — typically the textarea value. */
  value: string;
  /**
   * Caret offset within `value`. The hook reads this to decide where the
   * active `@`-trigger ends. Pass `value.length` for "always at the end".
   */
  caret: number;
  /** Async source of candidates. Required — no default network call. */
  fetcher: MentionCandidateFetcher;
  /** Optional limit forwarded to `rankCandidates`. Defaults to 20. */
  limit?: number;
  /** Optional clock injection for deterministic debounce tests. */
  setTimeoutImpl?: typeof setTimeout;
  clearTimeoutImpl?: typeof clearTimeout;
  /** Optional pre-computed recency map (e.g. recent-mentions cache). */
  recencyByEntityId?: ReadonlyMap<string, number> | Record<string, number>;
}

/**
 * Find the closest `@`-trigger to the left of `caret` that has not been
 * closed by whitespace, newline, or another `@`. Returns `null` when no
 * trigger is active.
 *
 * Trigger rules (UX §8.2 + W2-S7 AC #3):
 *   - the `@` must be at start-of-string OR preceded by whitespace
 *   - the prefix is the substring `@..caret` minus the `@`
 *   - whitespace, newline, or a second `@` between the trigger and the
 *     caret terminates the prefix → no active trigger
 *   - prefix length must be ≥ 0 (empty prefix opens the popup with empty
 *     state — same UX as Slack/Linear)
 */
export function findActiveMention(value: string, caret: number): MentionTrigger | null {
  if (caret < 0 || caret > value.length) return null;
  // Walk left from caret looking for `@`.
  for (let i = caret - 1; i >= 0; i--) {
    const ch = value.charAt(i);
    if (ch === '@') {
      // Validate boundary: start-of-string or preceded by whitespace.
      const prev = i === 0 ? '' : value.charAt(i - 1);
      if (prev === '' || /\s/.test(prev)) {
        return {
          atOffset: i,
          prefix: value.slice(i + 1, caret),
          caretOffset: caret,
        };
      }
      return null;
    }
    // Whitespace / newline closes the prefix window.
    if (/\s/.test(ch)) return null;
  }
  return null;
}

interface FetchState {
  prefix: string;
  candidates: ReadonlyArray<KbEntity>;
}

/**
 * Imperative ref-style API for keyboard handling — exposes the latest
 * `activeIndex`/`candidates` so the textarea can forward `onKeyDown`
 * without prop-drilling.
 */
export interface MentionAutocompleteRef {
  open: boolean;
  prefix: string;
  candidates: ReadonlyArray<MentionCandidate>;
  activeIndex: number;
  activeFilter: KbEntityKind | undefined;
}

/**
 * React hook driving the mention popup. See file header for behaviour.
 */
export function useMentionAutocomplete(
  options: UseMentionAutocompleteOptions,
): UseMentionAutocompleteResult {
  const {
    value,
    caret,
    fetcher,
    limit = MENTION_DEFAULT_LIMIT,
    setTimeoutImpl = setTimeout,
    clearTimeoutImpl = clearTimeout,
    recencyByEntityId,
  } = options;

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [activeFilter, setActiveFilter] = useState<KbEntityKind | undefined>(undefined);
  const [fetchState, setFetchState] = useState<FetchState>({ prefix: '', candidates: [] });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissedFor, setDismissedFor] = useState<number | null>(null);

  const trigger = useMemo(() => findActiveMention(value, caret), [value, caret]);

  // Suppress popup when the user pressed Esc and hasn't typed since.
  const suppressed = trigger !== null && dismissedFor === trigger.atOffset;
  // The `@` itself is always visible — open whenever a trigger exists and
  // hasn't been suppressed. (UX §8.2: open on `@`, refine as user types.)
  const open = trigger !== null && !suppressed;

  // Reset suppression whenever the trigger anchor moves.
  useEffect(() => {
    if (trigger === null) {
      setDismissedFor(null);
      return;
    }
    if (dismissedFor !== null && dismissedFor !== trigger.atOffset) {
      setDismissedFor(null);
    }
  }, [trigger, dismissedFor]);

  // Reset activeIndex when the candidate list or filter changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [fetchState.candidates, activeFilter]);

  // Debounced fetch.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const triggerOffsetRef = useRef<number | null>(null);
  triggerOffsetRef.current = trigger === null ? null : trigger.atOffset;

  useEffect(() => {
    // Tear down any pending debounce or in-flight fetch.
    if (debounceRef.current !== null) {
      clearTimeoutImpl(debounceRef.current);
      debounceRef.current = null;
    }
    if (abortRef.current !== null) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    if (!open || trigger === null) {
      // Don't clear `fetchState` immediately — keeps the last results
      // around for re-open without a flicker.
      setLoading(false);
      return undefined;
    }

    // Empty prefix: don't fetch (server rejects minLength 1) but clear
    // results so the popup shows the empty state.
    if (trigger.prefix.length === 0) {
      setFetchState({ prefix: '', candidates: [] });
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);
    const myAt = trigger.atOffset;

    debounceRef.current = setTimeoutImpl(() => {
      debounceRef.current = null;
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      void (async () => {
        try {
          const result = await fetcher(trigger.prefix, ctrl.signal);
          if (ctrl.signal.aborted) return;
          // Stale-trigger guard — discard responses for a different `@`.
          if (triggerOffsetRef.current !== myAt) return;
          setFetchState({ prefix: trigger.prefix, candidates: result });
          setError(null);
        } catch (err) {
          if (ctrl.signal.aborted) return;
          if (triggerOffsetRef.current !== myAt) return;
          const message = err instanceof Error ? err.message : 'Mention fetch failed';
          setError(message);
          setFetchState({ prefix: trigger.prefix, candidates: [] });
        } finally {
          if (triggerOffsetRef.current === myAt) {
            setLoading(false);
          }
        }
      })();
    }, MENTION_DEBOUNCE_MS);

    return (): void => {
      if (debounceRef.current !== null) {
        clearTimeoutImpl(debounceRef.current);
        debounceRef.current = null;
      }
    };
    // We deliberately depend on the prefix string (via `trigger.prefix`)
    // and the `@` offset rather than the trigger object identity. The
    // `react-hooks/exhaustive-deps` rule is not enabled in this package's
    // eslint config (W2-S2 settled the rule-set); the dep array is hand
    // verified instead.
  }, [open, trigger?.prefix, trigger?.atOffset, fetcher, setTimeoutImpl, clearTimeoutImpl]);

  // Cleanup on unmount.
  useEffect(() => {
    return (): void => {
      if (debounceRef.current !== null) clearTimeoutImpl(debounceRef.current);
      if (abortRef.current !== null) abortRef.current.abort();
    };
  }, [clearTimeoutImpl]);

  // Rank the latest candidates whenever they or the prefix change.
  const ranked = useMemo<ReadonlyArray<MentionCandidate>>(() => {
    if (!open || trigger === null || fetchState.prefix !== trigger.prefix) {
      // Either closed or the cached candidates belong to a stale prefix.
      return [];
    }
    return rankCandidates(fetchState.prefix, fetchState.candidates, {
      limit,
      recencyByEntityId,
    });
  }, [open, trigger, fetchState, limit, recencyByEntityId]);

  const dismiss = useCallback(() => {
    if (trigger !== null) setDismissedFor(trigger.atOffset);
  }, [trigger]);

  const applyReplacement = useCallback(
    (source: string, replacement: string): string => {
      if (trigger === null) return source;
      const before = source.slice(0, trigger.atOffset);
      const after = source.slice(trigger.caretOffset);
      return `${before}${replacement}${after}`;
    },
    [trigger],
  );

  return {
    open,
    prefix: trigger?.prefix ?? '',
    candidates: ranked,
    activeIndex,
    activeFilter,
    loading,
    error,
    trigger,
    applyReplacement,
    setActiveIndex,
    setActiveFilter,
    dismiss,
  };
}

/**
 * Convenience builder — wraps a synchronous candidate provider (e.g. an
 * in-memory cache) into the `MentionCandidateFetcher` shape required by
 * the hook. Useful for tests and offline workflows.
 */
export function syncFetcher(
  source: () => ReadonlyArray<KbEntity>,
): MentionCandidateFetcher {
  return async (prefix, signal) => {
    if (signal.aborted) return [];
    if (prefix.length === 0) return [];
    return source();
  };
}

// `findActiveMention` is exported above (function declaration). Composers
// can call it directly to detect the trigger window without instantiating
// the hook — useful for the "submit blocked when an unresolved `@text`
// is in the message" guard (UX §8.8).

// `RefObject` is exported as a typedef placeholder to keep the hook
// stylistically aligned with the W4-S5 / W4-S4 hooks that pass refs into
// the composer. Composers wishing to forward `onKeyDown` to the popup
// will type their textarea ref as `RefObject<HTMLTextAreaElement>`.
export type { RefObject };
